import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import { catchError, filter, map, switchMap, takeWhile, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import {
  CancelRefreshReportStatsAction,
  GET_REPORT_PROJECTS,
  GetReportProjectsFailureAction,
  GetReportProjectsSuccessAction,
  GetReportStatsFailureAction,
  GetReportStatsSuccessAction,
  REFRESH_REPORT_STATS,
  REFRESH_REPORT_STATS_REQUEST,
  RefreshReportStatsAction,
  RefreshReportStatsRequestAction,
  ReportsAction,
  SET_REPORT_FILTERS,
  SetReportFiltersAction
} from './reports.actions';
import { ProjectsService } from '../projects/projects.service';
import { ProjectResponse } from '../projects/project';
import { StatsService } from '../stats/stats.service';
import { ReportFilters } from './report-filters/ReportFilters';
import { StatsResponse } from '../stats/stats.model';
import { interval } from 'rxjs';
import { Store } from '@ngrx/store';
import { IState } from '../../shared/models/state';
import { getReportFiltersSelector } from './reports.reducer';
import { LEAD_GENERATION_STATS_REPORTS_REFRESH_SECONDS } from '../../shared/constants';
import * as moment from 'moment';
import { DateRange } from '../../shared/date-range-picker/date-range';

const isStartOfToday = (date: Date) => {
  const dateMoment = moment(date).startOf('day');
  const today = moment().startOf('day');
  return dateMoment.diff(today) === 0;
};

const obtainNewDateRange = (dateRange: DateRange) => {
  const { dateFrom } = dateRange;

  // when refreshing is running for long time the day can pass, but we want to show stats only from today
  const newDateFrom = isStartOfToday(dateFrom)
    ? dateFrom
    : moment()
        .startOf('day')
        .toDate();
  return { ...dateRange, dateFrom: newDateFrom, dateTo: new Date() };
};

@Injectable()
export class ReportsEffects {
  constructor(
    private actions$: Actions,
    private projectService: ProjectsService,
    private statsService: StatsService,
    private store: Store<IState>
  ) {}

  @Effect()
  getFilterProjects: Observable<ReportsAction> = this.actions$.pipe(
    ofType(GET_REPORT_PROJECTS),
    switchMap(() =>
      this.projectService
        .allProjects()
        .pipe(
          map((response: ProjectResponse[]) => new GetReportProjectsSuccessAction(response)),
          catchError(error => of(new GetReportProjectsFailureAction(error)))
        )
    )
  );

  @Effect()
  getReportStats: Observable<ReportsAction> = this.actions$.pipe(
    ofType(SET_REPORT_FILTERS),
    map((action: SetReportFiltersAction) => action.payload),
    switchMap((filters: ReportFilters) =>
      this.statsService
        .getStats(filters)
        .pipe(
          map((response: StatsResponse) => new GetReportStatsSuccessAction(response)),
          catchError(error => of(new GetReportStatsFailureAction(error)))
        )
    )
  );

  @Effect()
  startRefreshingReportStats: Observable<ReportsAction> = this.actions$.pipe(
    ofType(SET_REPORT_FILTERS),
    map((action: SetReportFiltersAction) => action.payload),
    withLatestFrom(this.store.select(getReportFiltersSelector)),
    filter(([updatedFilters, store]) => !store.liveModeEnabled && isStartOfToday(updatedFilters.dateRange.dateFrom)),
    map(() => new RefreshReportStatsRequestAction(LEAD_GENERATION_STATS_REPORTS_REFRESH_SECONDS))
  );

  @Effect()
  cancelRefreshingReportStats: Observable<ReportsAction> = this.actions$.pipe(
    ofType(SET_REPORT_FILTERS),
    map((action: SetReportFiltersAction) => action.payload),
    withLatestFrom(this.store.select(getReportFiltersSelector)),
    filter(([updatedFilters, store]) => store.liveModeEnabled && !isStartOfToday(updatedFilters.dateRange.dateFrom)),
    map(() => new CancelRefreshReportStatsAction())
  );

  @Effect()
  refreshReportStatsRequest: Observable<ReportsAction> = this.actions$.pipe(
    ofType(REFRESH_REPORT_STATS_REQUEST),
    map((action: RefreshReportStatsRequestAction) => action.timeInSeconds),
    switchMap((seconds: number) =>
      interval(seconds * 1000).pipe(
        withLatestFrom(this.store.select(getReportFiltersSelector)),
        takeWhile(([_, filters]) => filters.liveModeEnabled),
        map(() => new RefreshReportStatsAction())
      )
    )
  );

  @Effect()
  refreshReportStats: Observable<ReportsAction> = this.actions$.pipe(
    ofType(REFRESH_REPORT_STATS),
    withLatestFrom(this.store.select(getReportFiltersSelector)),
    map(([_, filters]) => {
      const dateRange = obtainNewDateRange(filters.dateRange);
      return new SetReportFiltersAction({
        ...filters,
        dateRange
      });
    })
  );
}
